import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { ref } from 'vue';
import type { ISportInfo } from '@/interface/ISportInfo';
import { SportType } from '@/interface/enum';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import { useSportsList } from '@/libraries/composables/useSportsList';
import BusinessLogicHelper from '@/libraries/helpers/businessLogicHelper';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useOddsApiStore } from '@/stores/oddsApiStore';
import type { SportInfoType } from '@/types';
import { useSportsInfoQuery as _useSportsInfoQuery } from '@/types';

export function useSportsInfoQuery(isEnabled: Ref<boolean>) {
    const sportsInfo = ref<ISportInfo[]>([]);

    const variable = deepCompareComputed(() => ({
        query: {},
    }));

    const { onResult, refetch, loading } = _useSportsInfoQuery(
        variable,
        deepCompareComputed(() => ({
            enabled: isEnabled.value,
            pollInterval: 30000,
        })),
    );

    const { customerSettings } = storeToRefs(useOddsApiStore());

    const { sortSportsList } = useSportsList();
    onResult((result) => {
        const filtered = result
            ?.map((sportInfo) => {
                const filteredSportInfo = BusinessLogicHelper.filterSportInfo(sportInfo, customerSettings.value);
                return formatSportInfo(filteredSportInfo);
            }) ?? [];

        sportsInfo.value = sortSportsList(filtered);
    });

    const { sportsWithNewTagConfig } = storeToRefs(useFeatureToggleStore());
    function formatSportInfo(sportInfo: SportInfoType): ISportInfo {
        return {
            sportType: SportType.fromOddsApi(sportInfo.sport),
            hasEvents: sportInfo.eventCount + sportInfo.outrightTournamentCount > 0,
            hasLiveEvents: sportInfo.liveEventCount + sportInfo.liveOutrightTournamentCount > 0,
            hasMixParlayEvents: sportInfo.mixParlayEventCount > 0,
            hasLiveMixParlayEvents: sportInfo.liveMixParlayEventCount > 0,
            isNew: sportsWithNewTagConfig.value.includes(SportType.fromOddsApi(sportInfo.sport)),
            infos: null,
        };
    }

    return {
        sportsInfo,
        loading,
        refetch,
    };
}
